import { useEffect, useMemo, useState } from "react";
import api, { openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CheckSquare, Square, Printer, PackageCheck, Send, Truck } from "lucide-react";
import LabelPickerButton from "../components/LabelPickerButton";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";

/**
 * Versand-Übersicht mit Bulk-Abfertigung und Direkt-Buchung bei DHL/DSV.
 */
export default function Shipping() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [alsoInvoice, setAlsoInvoice] = useState({});
  const [selected, setSelected] = useState({});
  const [bulkInvoice, setBulkInvoice] = useState(false);
  const [loading, setLoading] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const [ship, its] = await Promise.all([
        api.get("/shipments/ready"),
        api.get("/items?page_size=500"),
      ]);
      setRows(ship.data);
      setItems(its.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Laden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const itemById = useMemo(() => {
    const m = {}; items.forEach((i) => { m[i.id] = i; }); return m;
  }, [items]);

  const lineWeight = (line) => {
    const it = itemById[line.item_id];
    const w = Number(line.weight_g || it?.weight_g || 0);
    return w * (Number(line.quantity) || 0);
  };
  const totalWeightKg = (row) => (row.lines || []).reduce((s, l) => s + lineWeight(l), 0) / 1000;

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    const allOn = rows.length > 0 && rows.every((r) => selected[r.id]);
    const next = {};
    if (!allOn) rows.forEach((r) => { next[r.id] = true; });
    setSelected(next);
  };

  const printLabelAndFinalize = async (row) => {
    try {
      await openPdf(`/delivery-notes/${row.id}/shipping-label.pdf`).catch(() => {});
      await openPdf(`/delivery-notes/${row.id}/pdf`).catch(() => {});
      if (alsoInvoice[row.id]) {
        await openPdf(`/delivery-notes/${row.id}/invoice.pdf`).catch(() => {});
      }
      await api.post(`/delivery-notes/${row.id}/finalize-shipment`, {});
      toast.success(`Versand ${row.number} abgeschlossen`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const bulkFinalize = async () => {
    if (!selectedIds.length) return;
    const ok = await confirm({
      title: `${selectedIds.length} Sendung(en) abfertigen?`,
      description: `Für jede Sendung wird das Versandlabel + der Lieferschein${bulkInvoice ? " + die Rechnung" : ""} gedruckt und der Status auf „Versendet" gesetzt. Paletten werden automatisch dem Kundenkonto gutgeschrieben.`,
      confirmLabel: `${selectedIds.length}× abfertigen`,
      variant: "default",
    });
    if (!ok) return;
    try {
      // Zusammenfassungs-PDF: bulk-zip mit Lieferscheinen + Labels
      await openPdf(`/delivery-notes/bulk-zip?ids=${selectedIds.join(",")}&include_invoice=${bulkInvoice ? "true" : "false"}&include_label=true`).catch(() => {});
      const { data } = await api.post("/shipments/bulk-finalize", { ids: selectedIds });
      toast.success(`${data.processed.length}/${data.total} Sendungen abgeschlossen`);
      setSelected({});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const bookDhl = async (row) => {
    try {
      const w = Math.max(Math.round(totalWeightKg(row) * 1000) || 500, 1);
      const { data } = await api.post(`/delivery-notes/${row.id}/dhl-shipment`, { weight_grams: w });
      toast.success(`DHL gebucht · ${data.tracking_number}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "DHL-Fehler"); }
  };
  const bookDsv = async (row) => {
    try {
      const { data } = await api.post(`/delivery-notes/${row.id}/dsv-shipment`);
      toast.success(`DSV gebucht · ${data.tracking_number}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "DSV-Fehler"); }
  };

  return (
    <div>
      <PageHeader
        overline="Logistik"
        title="Versand"
        subtitle={`${rows.length} versandbereite Sendung${rows.length === 1 ? "" : "en"}`}
      />
      {selectedIds.length > 0 && (
        <div className="mb-4 flex items-center justify-between gap-4 px-4 py-3 bg-black text-white rounded-2xl"
             data-testid="shipping-bulk-toolbar">
          <div className="text-sm"><span className="font-bold">{selectedIds.length}</span> Sendung(en) ausgewählt</div>
          <div className="flex items-center gap-3">
            <label className="text-xs flex items-center gap-1.5 select-none">
              <Checkbox checked={bulkInvoice} onCheckedChange={(v) => setBulkInvoice(!!v)}
                        data-testid="checkbox-bulk-invoice" />
              + Rechnungen drucken
            </label>
            <Button size="sm" onClick={() => setSelected({})} variant="outline"
                    className="rounded-full bg-transparent text-white border-white/40 hover:bg-white/10"
                    data-testid="btn-clear-shipping-selection">Abwählen</Button>
            <Button size="sm" onClick={bulkFinalize}
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                    data-testid="btn-bulk-finalize">
              <Printer size={14} className="mr-1" /> Batch abfertigen ({selectedIds.length})
            </Button>
          </div>
        </div>
      )}
      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="overline w-10">
                <button onClick={toggleAll} data-testid="shipping-select-all" className="hover:opacity-70">
                  {rows.length > 0 && rows.every((r) => selected[r.id])
                    ? <CheckSquare size={16} className="text-[var(--pop-pink)]" />
                    : <Square size={16} />}
                </button>
              </TableHead>
              <TableHead className="overline">Kunde</TableHead>
              <TableHead className="overline">LS-Nr.</TableHead>
              <TableHead className="overline">Artikel</TableHead>
              <TableHead className="overline text-right">Gewicht</TableHead>
              <TableHead className="overline">Versanddatum</TableHead>
              <TableHead className="overline">Sendungsnummer</TableHead>
              <TableHead className="overline">Status</TableHead>
              <TableHead className="overline text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const kg = totalWeightKg(r);
              return (
                <TableRow key={r.id} data-testid={`shipping-row-${r.number}`}>
                  <TableCell>
                    <button onClick={() => toggleOne(r.id)} data-testid={`select-shipping-${r.number}`}>
                      {selected[r.id]
                        ? <CheckSquare size={16} className="text-[var(--pop-pink)]" />
                        : <Square size={16} />}
                    </button>
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-baseline gap-2">
                      {r.customer_number && (
                        <span className="font-mono text-[11px] text-[var(--text-muted)]">{r.customer_number}</span>
                      )}
                      <span>{r.customer_name || "—"}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.number}</TableCell>
                  <TableCell className="text-xs text-[var(--text-muted)]">
                    {(r.lines || []).length} Pos.
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {kg > 0 ? `${kg.toFixed(2)} kg` : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {(r.shipping_date || r.shipped_at || "").slice(0, 10) || "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.tracking_number || r.dhl_tracking_number || r.dsv_tracking_number || "—"}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <label className="text-[10px] text-[var(--text-muted)] flex items-center gap-1 select-none">
                        <Checkbox
                          checked={!!alsoInvoice[r.id]}
                          onCheckedChange={(v) => setAlsoInvoice((s) => ({ ...s, [r.id]: !!v }))}
                          data-testid={`checkbox-invoice-${r.number}`}
                        />
                        + Rg.
                      </label>
                      {!r.dhl_tracking_number && (
                        <button onClick={() => bookDhl(r)}
                                data-testid={`btn-dhl-${r.number}`}
                                title="DHL buchen"
                                className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#FFCC00] hover:underline">
                          <Send size={12} /> DHL
                        </button>
                      )}
                      {!r.dsv_tracking_number && (
                        <button onClick={() => bookDsv(r)}
                                data-testid={`btn-dsv-${r.number}`}
                                title="DSV buchen"
                                className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#003580] hover:underline">
                          <Truck size={12} /> DSV
                        </button>
                      )}
                      <Button size="sm"
                              onClick={() => printLabelAndFinalize(r)}
                              data-testid={`btn-print-label-${r.number}`}
                              className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                        <Printer size={14} className="mr-1" /> Label
                      </Button>
                      <LabelPickerButton
                        testid={`label-picker-${r.number}`}
                        label="Etikett"
                        category="versand"
                        data={{
                          order_number: r.number,
                          customer_name: r.customer_name,
                          delivery_date: (r.delivery_date || "").slice(0, 10),
                          barcode: r.tracking_number || r.number,
                        }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-[var(--text-muted)]">
                  <PackageCheck size={32} className="mx-auto mb-2 opacity-40" />
                  Keine versandbereiten Sendungen. Markiere Lieferscheine mit „Zum Versand".
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
